import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, EquipmentItem, EquipmentDetail } from "@/lib/types"

interface EquipmentFilters {
  search?: string
  company_id?: string
  category?: string
  is_active?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: EquipmentFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useEquipment(filters: EquipmentFilters = {}) {
  return useQuery({
    queryKey: ["equipment", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<EquipmentItem>>(`/api/v1/equipment${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useEquipmentItem(id?: string) {
  return useQuery({
    queryKey: ["equipment", id],
    queryFn: () => api.get<{ data: EquipmentDetail }>(`/api/v1/equipment/${id}`),
    enabled: !!id,
  })
}

export function useCreateEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: EquipmentDetail }>("/api/v1/equipment", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment"] }),
  })
}

export function useUpdateEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: EquipmentDetail }>(`/api/v1/equipment/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment"] }),
  })
}

export function useDeleteEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/equipment/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["equipment"] }),
  })
}

// Ticket-Equipment linking
export function useTicketEquipment(ticketId?: string) {
  return useQuery({
    queryKey: ["service_tickets", ticketId, "equipment"],
    queryFn: () => api.get<{ data: EquipmentItem[] }>(`/api/v1/service_tickets/${ticketId}/equipment`),
    enabled: !!ticketId,
  })
}

export function useAddTicketEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, equipment_id, notes }: { ticketId: string; equipment_id: string; notes?: string }) =>
      api.post(`/api/v1/service_tickets/${ticketId}/equipment`, { equipment_id, notes }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "equipment"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}

export function useRemoveTicketEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, equipmentId }: { ticketId: string; equipmentId: string }) =>
      api.delete(`/api/v1/service_tickets/${ticketId}/equipment/${equipmentId}`),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId, "equipment"] })
      qc.invalidateQueries({ queryKey: ["service_tickets", vars.ticketId] })
    },
  })
}
